import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { SerpixWordmark } from "../brand/SerpixWordmark";
import { Button } from "../ui/button";
import { TEST_IDS } from "../../constants/testIds";
import { useAuthStore } from "../../store/authStore";
import { Menu, X } from "lucide-react";
import { cn } from "@/lib/utils";

export function LandingNav() {
  const { t, i18n } = useTranslation();
  const setLanguage = useAuthStore((s) => s.setLanguage);
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const toggleLang = () => {
    const next = i18n.language === "tr" ? "en" : "tr";
    setLanguage(next);
  };

  const navLinks = [
    { href: "#features", label: t("landing.nav.features") },
    { href: "#pricing", label: t("landing.nav.pricing") },
    { href: "#faq", label: t("landing.nav.faq") },
  ];

  return (
    <header
      data-testid="landing-nav"
      className={cn(
        "sticky top-0 z-50 w-full transition-[background-color,border-color,box-shadow] duration-300",
        scrolled
          ? "border-b border-zinc-200/70 bg-white/85 backdrop-blur-md shadow-sm"
          : "border-b border-transparent bg-transparent"
      )}
      style={{ paddingTop: "env(safe-area-inset-top)" }}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 lg:px-8">
        <Link to="/" aria-label="Serpix" data-testid="landing-nav-logo">
          <SerpixWordmark size="md" />
        </Link>

        <nav className="hidden items-center gap-8 md:flex">
          {navLinks.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`landing-nav-${l.href.slice(1)}`}
              className="text-sm font-medium text-zinc-600 transition-colors hover:text-zinc-950"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <button
            data-testid={TEST_IDS.langSwitch}
            onClick={toggleLang}
            className="hidden h-9 items-center rounded-full border border-zinc-200 bg-white/60 px-3 text-xs font-semibold uppercase tracking-wider text-zinc-600 transition-colors hover:border-zinc-300 hover:text-zinc-900 md:inline-flex"
          >
            {i18n.language === "tr" ? "EN" : "TR"}
          </button>
          <Link to="/login" data-testid={TEST_IDS.landingLoginBtn}>
            <Button
              variant="ghost"
              className="h-9 px-3 text-sm text-zinc-700 hover:text-zinc-950 md:h-10 md:px-4"
            >
              {t("landing.nav.login")}
            </Button>
          </Link>
          <Link to="/register" data-testid={TEST_IDS.landingRegisterBtn} className="hidden sm:block">
            <Button className="bg-zinc-950 text-white shadow-lg shadow-zinc-950/20 hover:bg-zinc-900">
              {t("landing.nav.signup")}
            </Button>
          </Link>
          <button
            className="ml-1 flex h-9 w-9 items-center justify-center rounded-full border border-zinc-200 md:hidden"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label="Toggle menu"
            data-testid="landing-nav-mobile-toggle"
          >
            {mobileOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="border-t border-zinc-200 bg-white md:hidden">
          <div className="mx-auto flex max-w-7xl flex-col gap-1 px-6 py-4">
            {navLinks.map((l) => (
              <a
                key={l.href}
                href={l.href}
                className="rounded-lg px-3 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-100"
                onClick={() => setMobileOpen(false)}
              >
                {l.label}
              </a>
            ))}
            <div className="my-2 h-px bg-zinc-100" />
            <Link
              to="/login"
              data-testid="landing-nav-mobile-login"
              onClick={() => setMobileOpen(false)}
              className="rounded-lg px-3 py-2 text-sm font-semibold text-zinc-900 hover:bg-zinc-100"
            >
              {t("landing.nav.login")}
            </Link>
            <Link
              to="/register"
              data-testid="landing-nav-mobile-signup"
              onClick={() => setMobileOpen(false)}
              className="rounded-lg bg-zinc-950 px-3 py-2 text-center text-sm font-semibold text-white shadow-md shadow-zinc-950/20 hover:bg-zinc-900"
            >
              {t("landing.nav.signup")}
            </Link>
            <button
              onClick={toggleLang}
              className="mt-2 inline-flex h-9 items-center justify-center rounded-full border border-zinc-200 px-3 text-xs font-semibold uppercase tracking-wider text-zinc-600"
            >
              {i18n.language === "tr" ? "English" : "Türkçe"}
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
